/**
 * All money is an integer number of fils. 1 AED = 100 fils. Never floats.
 */

export const FILS_PER_AED = 100

export function formatMoney(fils: number): string {
  const negative = fils < 0
  const abs = Math.abs(Math.round(fils))
  const whole = Math.floor(abs / FILS_PER_AED)
  const cents = abs % FILS_PER_AED
  const body = `${whole.toLocaleString('en-US')}.${String(cents).padStart(2, '0')}`
  return negative ? `-${body}` : body
}

export function formatAed(fils: number): string {
  return `${formatMoney(fils)} AED`
}

export function formatSigned(fils: number, withCurrency = false): string {
  const body = fils >= 0 ? `+${formatMoney(fils)}` : formatMoney(fils)
  return withCurrency ? `${body} AED` : body
}

/**
 * Parses user input or scraped text into fils.
 * Handles "1,234.56", "1.234,56", "1234", "AED 1 234.56", "$99.99".
 * Returns null when no number can be found.
 */
export function parseMoney(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined) return null

  let s = raw.trim().replace(/[\s  ]/g, '')
  if (!s) return null

  const negative = s.startsWith('-') || s.startsWith('−')
  s = s.replace(/[^0-9.,]/g, '')
  if (!s) return null

  const decimalIndex = Math.max(s.lastIndexOf('.'), s.lastIndexOf(','))
  let integerPart: string
  let fraction: string

  if (decimalIndex >= 0) {
    const tail = s.slice(decimalIndex + 1)
    if (tail.length >= 1 && tail.length <= 2 && /^\d+$/.test(tail)) {
      integerPart = s.slice(0, decimalIndex)
      fraction = tail.padEnd(2, '0')
    } else {
      // A separator followed by 3+ digits is a thousands separator.
      integerPart = s
      fraction = '00'
    }
  } else {
    integerPart = s
    fraction = '00'
  }

  integerPart = integerPart.replace(/[.,]/g, '')
  if (!integerPart) integerPart = '0'
  if (!/^\d+$/.test(integerPart)) return null

  const fils = Number(integerPart) * FILS_PER_AED + Number(fraction)
  if (!Number.isSafeInteger(fils)) return null
  return negative ? -fils : fils
}
